<template>
  <div>
    <ca-heading text="Cálculo de rescisão contratual" />

    <ca-row v-if="!isValidPeriod && boxInformationPeriodInfo">
      <ca-col>
        <ca-box-information :alert.sync="boxInformationPeriodInfo"></ca-box-information>
      </ca-col>
    </ca-row>

    <ca-page class="ca-u-min-margin-top">
      <ca-row>
        <ca-col>
          <ca-box-information :alert.sync="rescissionAlert"></ca-box-information>
        </ca-col>
      </ca-row>

      <ca-row>
        <ca-col>
          <ca-box-information
            :alert.sync="boxInformationErrorMessage">
          </ca-box-information>
        </ca-col>
      </ca-row>

      <ca-loader v-if="showLoading"></ca-loader>

      <ca-row v-else>
        <ca-col>
          <ca-form
            :key="formKey"
            :is-valid.sync="isFormValid"
            :on-submit="onCalculate"
            :on-submit-success="onCalculateResolved"
            :on-submit-error="onCalculateError"
            :error-message="errorMessage">
            <ca-row>
              <ca-col size="6">
                <worker-select
                  v-model="termination.trabalhador.id"
                  required
                  @change="refreshForm"></worker-select>
              </ca-col>

              <ca-col size="6">
                <ca-field label="Causa">
                  <ca-search-select
                    v-model="termination.causa.name"
                    :on-change="onReasonChange">
                    <ca-option
                      v-for="option in misc.terminationCause"
                      :key="option.name"
                      :value="option.name">
                      {{ option.description }}
                    </ca-option>
                  </ca-search-select>
                </ca-field>
              </ca-col>

              <ca-col size="6">
                <ca-field label="Data da Rescisão">
                  <ca-date-input v-model="termination.dtRescisao" required></ca-date-input>
                </ca-field>
              </ca-col>

              <ca-col size="6">
                <ca-field label="Data do Pagamento">
                  <ca-date-input
                    v-model="termination.dtPagamento"
                    :on-change="checkPeriod"
                    required></ca-date-input>
                </ca-field>
              </ca-col>

              <ca-col size="6">
                <ca-field label="Saldo do FGTS">
                  <ca-input-currency v-model="termination.saldoFgts"></ca-input-currency>
                </ca-field>
              </ca-col>

              <ca-col size="6">
                <ca-field label="Status do Desligamento no eSocial">
                  <ca-select v-model="termination.statusEsocial.name" :on-change="refreshForm">
                    <ca-option
                      v-for="option in misc.esocialStatus.default"
                      :key="option.name"
                      :value="option.name">
                      {{ option.description }}
                    </ca-option>
                  </ca-select>
                </ca-field>
              </ca-col>

              <ca-col size="6">
                <ca-field label="Tipo de Aviso">
                  <ca-select
                    v-model="termination.tipoAviso.name"
                    required
                    @change="getEarlyWarningDate">
                    <ca-option
                      v-for="option in earlyWarningTypes"
                      :key="option.name"
                      :value="option.name">
                      {{ option.description }}
                    </ca-option>
                  </ca-select>
                </ca-field>
              </ca-col>

              <ca-col size="6">
                <ca-field label="Data do Aviso Prévio">
                  <ca-date-input
                    v-model="termination.dtAvisoPre"
                    :disabled="!shouldShowEarlyWarning"
                    :required="shouldShowEarlyWarning">
                  </ca-date-input>
                </ca-field>
              </ca-col>

              <ca-col v-if="shouldShowEarlyWarning" size="6">
                <ca-field label="Indicador de Cumprimento do Aviso">
                  <ca-select
                    v-model="termination.tipoCumprimentoAviso.name"
                    :on-change="refreshForm">
                    <ca-option
                      v-for="option in misc.warningFulfillmentTypes"
                      :key="option.name"
                      :value="option.name">
                      {{ option.description }}
                    </ca-option>
                  </ca-select>
                </ca-field>
              </ca-col>
            </ca-row>

            <ca-row>
              <ca-col>
                <ca-field :label="reasonField.label">
                  <ca-textarea
                    v-model="termination.motivo"
                    :placeholder="reasonField.placeholder"></ca-textarea>
                </ca-field>
              </ca-col>
            </ca-row>

            <ca-row>
              <ca-col>
                <ca-heading size="small" text="Opções de cálculo"></ca-heading>
              </ca-col>
            </ca-row>

            <ca-row vertical-offset="1">
              <ca-col
                v-for="(option, index) in calcOptions"
                :key="index"
                size="4">
                <ca-checkbox
                  v-model="termination.opcoesRescisao[option.field]"
                  :label="option.label">
                </ca-checkbox>
              </ca-col>
            </ca-row>

            <ca-footer>
              <ca-row vertical-offset="2">
                <ca-col size="2">
                  <ca-button full-width @click="goBack">
                    Voltar
                  </ca-button>
                </ca-col>
                <ca-col offset="6" size="2">
                  <ca-request-button
                    :disabled="!isFormValid"
                    :on-request="onSimulateTermination"
                    :on-request-success="onSimulateTerminationSuccess"
                    :on-request-error="onCalculateError"
                    theme="secondary">
                    Simular rescisão
                  </ca-request-button>
                </ca-col>
                <ca-col size="2">
                  <div v-ca-tooltip="terminationButtonTooltipMessage">
                    <ca-submit-button :disabled="!isValidPeriod">
                      Calcular rescisão
                    </ca-submit-button>
                  </div>
                </ca-col>
              </ca-row>
            </ca-footer>
          </ca-form>
        </ca-col>
      </ca-row>
    </ca-page>
  </div>
</template>

<script>
import WorkerSelect from 'packages/components/common-form-selects/WorkerSelect';
import {
  initFormObject,
  getBoxInformationCalculationMessageError,
} from 'packages/common/utils/utils';
import { checkPeriodMixin } from 'packages/common/mixins/check-period/CheckPeriodMixin.js';
import TerminationViewRollover from './modals/termination-view-rollover/TerminationViewRollover';
import {
  mountTerminationCalcOptions,
  createTerminations, findWarningDate, getReasonLabelAndPlaceholder, getTermination,
  getFormattedObjectOnResponse, redoTerminations, updateTerminationCalcOptions, isReasonContractEnd,
} from './terminationService';
import {
  WARNING_TYPES, EARLY_WARNING_TYPES, BOX_INFORMATION_RESCISSION,
} from './constants';
import { clickSimulateTerminationTracker } from './terminationTrackerService';

export default {
  name: 'TerminationFormPage',
  components: {
    WorkerSelect,
  },
  mixins: [checkPeriodMixin({ initialCheck: false })],
  data() {
    return {
      showLoading: false,
      rescissionAlert: BOX_INFORMATION_RESCISSION,
      isFormValid: true,
      formKey: 0,
      terminationId: this.$route.params.id,
      termination: {
        ...initFormObject([
          { causa: 'O02' },
          { statusEsocial: 'N' },
          { tipoAviso: 'I' },
          { tipoCumprimentoAviso: 'T' },
        ]),
        dtAvisoPre: null,
        trabalhador: { id: null },
        opcoesRescisao: {
          calcSalSal: true,
          calcFerVen: true,
          calcFerPro: true,
          calcDecPro: true,
          calcSalFam: true,
        },
      },
      calcOptions: mountTerminationCalcOptions(),
      reasonField: getReasonLabelAndPlaceholder(),
      isReasonContractEnd: false,
      errorMessage: null,
      boxInformationErrorMessage: null,
    };
  },
  computed: {
    hasTerminationId() {
      return !!this.terminationId;
    },
    shouldShowEarlyWarning() {
      return this.termination.tipoAviso.name === WARNING_TYPES.WORKING_HOURS_REDUCTION
        || this.termination.tipoAviso.name === WARNING_TYPES.WORKING_DAYS_REDUCTION;
    },
    terminationCause() {
      return this.termination.causa.name;
    },
    misc() {
      return this.$store.state.misc;
    },
    earlyWarningTypes() {
      if (this.isReasonContractEnd) {
        return this.misc.earlyWarningTypes
          .filter(value => value.description === EARLY_WARNING_TYPES.DISPENSED.description);
      }

      return this.misc.earlyWarningTypes;
    },
    terminationButtonTooltipMessage() {
      return this.isValidPeriod
        ? ''
        : 'Você está dentro de um período fechado e não é possível realizar a recisão';
    },
  },
  watch: {
    shouldShowEarlyWarning() {
      this.refreshForm();
    },
    terminationCause() {
      updateTerminationCalcOptions(this.termination);
    },
  },
  created() {
    this.initData();
  },
  methods: {
    initData() {
      this.$store.dispatch('misc/getTerminationCause');
      this.$store.dispatch('misc/getEarlyWarningTypes');
      this.$store.dispatch('misc/getEsocialStatus');
      this.$store.dispatch('misc/getWarningFulfillmentTypes');

      if (this.hasTerminationId) {
        this.setTermination();
      }
    },
    checkPeriod() {
      this.checkPeriodValidation(this.termination.dtPagamento);
    },
    async setTermination() {
      this.showLoading = true;
      try {
        this.termination = {
          ...this.termination,
          ...await getTermination(this.terminationId),
        };
        this.termination = getFormattedObjectOnResponse(this.termination);
        this.isReasonContractEnd = isReasonContractEnd(this.termination.causa.name);
      } catch (e) {
        this.$toaster.error('Aconteceu um erro, por favor tente novamente.');
        this.goBack();
      } finally {
        this.showLoading = false;
      }
    },
    async getEarlyWarningDate() {
      const { trabalhador, dtRescisao } = this.termination;

      if (this.shouldShowEarlyWarning && trabalhador.id && dtRescisao) {
        const response = await findWarningDate(trabalhador.id, dtRescisao);
        this.$set(this.termination, 'dtAvisoPre', response);
      } else {
        this.$set(this.termination, 'dtAvisoPre', null);
      }

      this.refreshForm();
    },
    async onReasonChange(reason) {
      this.reasonField = getReasonLabelAndPlaceholder(reason);
      this.isReasonContractEnd = isReasonContractEnd(reason);

      if (this.isReasonContractEnd) {
        this.termination.tipoAviso.name = EARLY_WARNING_TYPES.DISPENSED.value;
      }

      await this.$nextTick();
      this.refreshForm();
    },
    refreshForm() {
      this.boxInformationErrorMessage = null;
      this.formKey++;
    },
    goBack() {
      this.$router.back();
    },
    onSimulateTermination() {
      clickSimulateTerminationTracker();

      const termination = {
        simulacao: true,
        ...this.termination,
      };
      return createTerminations(this.termination.trabalhador.id, termination);
    },
    onSimulateTerminationSuccess(response) {
      this.$dialogModal(TerminationViewRollover, {
        props: {
          isSimulation: true,
          terminationResponse: response,
          terminationPayload: this.termination,
        },
      }).open();
    },
    calculateTerminations() {
      return this.hasTerminationId
        ? redoTerminations(this.termination.trabalhador.id, this.termination)
        : createTerminations(this.termination.trabalhador.id, this.termination);
    },
    onCalculate() {
      this.boxInformationErrorMessage = null;
      return this.calculateTerminations();
    },
    onCalculateResolved({ cancelCalculate } = {}) {
      if (!cancelCalculate) {
        this.onCalculateSuccess();
      }
    },
    onCalculateSuccess() {
      this.goBack();
      this.$toaster.success('Dados salvos com sucesso');
    },
    onCalculateError(error) {
      this.boxInformationErrorMessage = getBoxInformationCalculationMessageError(error);
    },
  },
};
</script>
